<template>
  <div class="ios-download-page">
    <!-- 步骤条：仅在 UDID/注册/下载页显示 -->
    <div v-show="currentScreen >= 1" class="stepper">
      <div
        v-for="(item, index) in steps"
        :key="index"
        class="step-pill"
        :class="{ 'is-active': currentScreen === index + 1, 'is-done': currentScreen > index + 1 }"
      >
        <span class="dot">
          <span v-if="currentScreen > index + 1" class="check"></span>
          <span v-else class="num">{{ index + 1 }}</span>
        </span>
        <span class="label">{{ item }}</span>
      </div>
    </div>

    <div v-show="currentScreen >= 1 && (currentScreen !== 3 || isReturningUser)" class="page-nav">
      <div class="nav-back" @click="goBack">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M10 13L5 8l5-5"/></svg>
        返回
      </div>
      <div class="nav-title">{{ navTitle }}</div>
    </div>

    <div class="content">
      <!-- Screen 0: 入口选择 -->
      <div v-show="currentScreen === 0" class="screen">
        <div class="hero" style="text-align: center; margin-top: 16px">
          <div class="eyebrow">iOS 签名安装</div>
          <h1 style="font-size: 26px; line-height: 1.2">开始安装</h1>
          <p class="lead">请选择你当前的设备状态，以便我们为你匹配最快的安装路径。</p>
        </div>

        <div class="entry-cards">
          <div class="entry-card primary" @click="chooseNewDevice">
            <div class="head">
              <div class="icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>
              </div>
              <div class="title">首次在这台设备安装</div>
              <svg class="arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 3l5 5-5 5"/></svg>
            </div>
            <div class="desc">需要获取 UDID 并完成注册绑定</div>
          </div>

          <div class="entry-card secondary" @click="chooseReturning">
            <div class="head">
              <div class="icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div class="title">已提交过 UDID</div>
              <svg class="arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 3l5 5-5 5"/></svg>
            </div>
            <div class="desc">直接前往个人签名下载页</div>
          </div>

          <div class="entry-card secondary" @click="() => goToScreen(3)">
            <div class="head">
              <div class="icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div class="title">可以接受不稳定的企业签</div>
              <svg class="arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 3l5 5-5 5"/></svg>
            </div>
            <div class="desc">直接前往企业签下载页</div>
          </div>
        </div>

        <div class="card" style="margin-top: 20px">
          <div style="display: flex; gap: 10px; align-items: flex-start">
            <van-icon name="question-o" style="color: #07c160; font-size: 18px; flex-shrink: 0; margin-top: 1px" />
            <div class="card-hint">不确定是否提交过？首次安装必须完成 UDID 获取与注册，否则企业签无法定向签发到你当前设备。如果之前在其他页面提交过相同设备的 UDID，可直接选择「已提交过」。</div>
          </div>
        </div>
      </div>

      <!-- Screen 1: Automated UDID Retrieval -->
      <div v-show="currentScreen === 1" class="screen">
        <div class="hero">
          <div class="eyebrow">STEP 01 / 03</div>
          <h1>获取设备 UDID</h1>
          <p class="lead">点击下方按钮下载描述文件，iOS 会自动识别你的设备</p>
        </div>

        <!-- Polling state -->
        <div v-if="isPolling" class="polling-indicator">
          <div class="spinner"></div>
          <div class="polling-text">
            <strong>等待设备响应...</strong><br>
            请在 Safari 中安装描述文件，iOS 会自动发送设备信息到服务器
          </div>
        </div>

        <!-- Download button -->
        <div v-else>
          <a class="van-button van-button--primary van-button--round action-btn"
             :href="profileDownloadUrl || '#'"
             @click.prevent="startUdidRetrieval">
            <svg class="btn-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M8 2v9"/>
              <path d="M4.5 7.5L8 11l3.5-3.5"/>
              <path d="M2.5 13.5h11"/>
            </svg>
            下载描述文件获取 UDID
          </a>

          <div class="warn-card">
            <van-icon name="warning" class="warn-icon" />
            <div class="warn-text">
              <strong>务必使用 Safari 浏览器</strong>
              微信/QQ 内置浏览器无法安装描述文件。下载后请在 Safari 中打开并安装。
            </div>
          </div>

          <!-- Profile installation guide -->
          <div class="install-guide">
            <div class="install-guide-title">描述文件安装步骤</div>
            <div class="install-guide-steps">
              <div class="guide-step">
                <span class="guide-step-num">1</span>
                <div class="guide-step-content">
                  <strong>点击下载</strong>
                  <p>点击上方按钮，系统会提示下载描述文件</p>
                </div>
              </div>
              <div class="guide-step">
                <span class="guide-step-num">2</span>
                <div class="guide-step-content">
                  <strong>允许下载</strong>
                  <p>在弹窗中点击「允许」，描述文件将开始下载</p>
                </div>
              </div>
              <div class="guide-step">
                <span class="guide-step-num">3</span>
                <div class="guide-step-content">
                  <strong>打开设置</strong>
                  <p>下载完成后，打开「设置」→「已下载的描述文件」</p>
                </div>
              </div>
              <div class="guide-step">
                <span class="guide-step-num">4</span>
                <div class="guide-step-content">
                  <strong>安装描述文件</strong>
                  <p>点击描述文件 →「安装」→ 输入锁屏密码确认</p>
                </div>
              </div>
              <div class="guide-step">
                <span class="guide-step-num">5</span>
                <div class="guide-step-content">
                  <strong>完成</strong>
                  <p>安装成功后，页面会自动检测到你的设备 UDID</p>
                </div>
              </div>
            </div>
            <div class="install-guide-note">
              <van-icon name="info-o" style="color: #07c160; margin-right: 6px" />
              整个过程约 10-20 秒，安装后无需重启设备
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="divider-or">
          <span>或</span>
        </div>

        <!-- Manual fallback -->
        <button v-if="!isManualMode" class="van-button van-button--default manual-btn" @click="goToScreenManual(1)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M12 3H4a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1z"/>
            <path d="M6 7h4M6 10h2"/>
          </svg>
          手动粘贴 UDID（高级用户）
        </button>

        <!-- Manual paste section -->
        <div v-else class="manual-paste-section">
          <div class="field" style="margin-top: 24px">
            <div class="field-label">
              <span><span class="req">*</span>粘贴你的 UDID</span>
              <span class="hint">25 位 / 40 位 hex</span>
            </div>
            <van-field
              v-model="udid"
              type="textarea"
              rows="2"
              placeholder="例如  00008030-001234567890ABCDEF"
              :class="{ 'is-invalid': udidError }"
              @update:model-value="onUdidInput"
            />
            <div v-if="udidError" class="field-error">
              <van-icon name="warning-o" /> {{ udidError }}
            </div>
            <div class="field-help">UDID 格式形如 <code>00008030-001A2B3C0123456789</code>，仅用于设备签名绑定。</div>
          </div>

          <a class="van-button van-button--primary van-button--round action-btn" href="https://udid.my" target="_blank" rel="noopener" @click="onOpenUdid" style="margin-top: 16px">
            <svg class="btn-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M9.5 3h3.5v3.5"/>
              <path d="M13 3l-6 6"/>
              <path d="M11 8.5V12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1h3.5"/>
            </svg>
            前往 udid.my 获取
          </a>
        </div>

        <div class="card" style="margin-top: 20px">
          <div style="display: flex; gap: 10px; align-items: flex-start">
            <van-icon name="question-o" style="color: #07c160; font-size: 18px; flex-shrink: 0; margin-top: 1px" />
            <div class="card-hint">
              描述文件安装后，iOS 会自动将设备信息发送到服务器，无需手动复制粘贴。
              整个过程通常在 10-20 秒内完成。
            </div>
          </div>
        </div>
      </div>

      <!-- Screen 2: 注册账号 -->
      <div v-show="currentScreen === 2" class="screen">
        <div class="hero">
          <div class="eyebrow">STEP 02 / 03</div>
          <h1>注册并绑定设备</h1>
          <p class="lead">UDID 已识别。请填写注册信息，服务端会将此 UDID 写入企业证书签发列表。</p>
        </div>

        <div class="field-label" style="margin-top: 14px">
          <span>已识别的 UDID</span>
          <span class="edit-link" @click="goToScreen(1)">重新粘贴</span>
        </div>
        <div class="chip-readonly">
          <van-icon name="lock" class="lock-icon" />
          <div class="stack">
            <div class="chip-label">UDID</div>
            <div class="chip-value">{{ udid || '—' }}</div>
          </div>
        </div>

        <div class="field">
          <div class="field-label">
            <span><span class="req">*</span>手机号</span>
            <span class="hint">中国大陆 +86</span>
          </div>
          <van-field
            v-model="mobile"
            type="tel"
            placeholder="请输入 11 位手机号"
            maxlength="11"
            :class="{ 'is-invalid': mobileError }"
            @update:model-value="onMobileInput"
          />
          <div v-if="mobileError" class="field-error">{{ mobileError }}</div>
        </div>

        <div class="field">
          <div class="field-label">
            <span><span class="req">*</span>用户名</span>
            <span class="hint">3–16 位字母/数字</span>
          </div>
          <van-field
            v-model="userName"
            placeholder="设置用户名"
            maxlength="16"
            :class="{ 'is-invalid': userNameError }"
            @update:model-value="onUserNameInput"
          />
          <div v-if="userNameError" class="field-error">{{ userNameError }}</div>
        </div>

        <div class="field">
          <div class="field-label">
            <span><span class="req">*</span>密码</span>
            <span class="hint">至少 6 位</span>
          </div>
          <van-field
            v-model="password"
            type="password"
            placeholder="设置登录密码"
            maxlength="32"
            :class="{ 'is-invalid': passwordError }"
            @update:model-value="onPasswordInput"
          />
          <div v-if="passwordError" class="field-error">{{ passwordError }}</div>
        </div>

        <div class="meta-row">
          <van-icon name="passed" color="#07c160" />
          注册即完成 UDID 绑定与证书签发
          <span class="dot-sep"></span>
          数据加密传输
        </div>
      </div>

      <!-- Screen 3: 下载安装 -->
      <div v-show="currentScreen === 3" class="screen">
        <a class="van-button van-button--primary van-button--round action-btn" :href="downloadUrl" @click="onDownload">
          <svg class="btn-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 2v9"/><path d="M4.5 7.5L8 11l3.5-3.5"/><path d="M2.5 13.5h11"/></svg>
          立即下载安装
        </a>

        <div class="install-steps">
          <div class="step">
            <span class="step-n">1</span>
            <div class="step-body">
              <h4>点击「安装」确认</h4>
              <p>系统弹窗显示「<strong>是否要安装"他趣秒中"</strong>」时，点击 安装，回到桌面查看下载进度。</p>
            </div>
          </div>
          <div v-if="!isReturningUser" class="step">
            <span class="step-n">2</span>
            <div class="step-body">
              <h4>信任企业开发者</h4>
              <p>首次启动会提示「未受信任的企业级开发者」，前往 设置 → 通用 → VPN与设备管理，点开发者名称后选择 信任。</p>
            </div>
          </div>
          <div class="step">
            <span class="step-n">{{ isReturningUser ? '2' : '3' }}</span>
            <div class="step-body">
              <h4>启动应用</h4>
              <p>回到桌面打开 APP 即可使用。如出现「无法验证」，请检查网络后重新打开。</p>
            </div>
          </div>
        </div>

        <div class="help-card">
          <div class="help-title">下载链接打不开？</div>
          <van-button type="default" round size="small" class="copy-btn" @click="copyLink">
            <van-icon name="description" /> 复制 itms-services 链接
          </van-button>
          <div class="help-text">或加 QQ 群 <strong style="color: #07c160">1031191163</strong> 联系群主处理</div>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="footer">
      <!-- Screen 0 无底部按钮 -->

      <van-button
        v-show="currentScreen === 1"
        type="primary"
        round
        block
        :disabled="!isValidUdid"
        @click="goToScreen(2)"
      >下一步 · 注册账号</van-button>

      <van-button
        v-show="currentScreen === 2"
        type="primary"
        round
        block
        :loading="submitting"
        :disabled="!canRegister"
        @click="doRegister"
      >{{ submitting ? '注册中…' : '立即注册并绑定' }}</van-button>

      <van-button
        v-show="currentScreen === 3"
        type="default"
        round
        block
        @click="resetFlow"
      >换一个设备 / 重新绑定</van-button>
    </div>

    <van-toast v-model:show="toast.show" :message="toast.message" />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import API from '@/api';
import config from "@/config";
import {getUrlParam} from "@/utils/util";

export default {
  name: 'iOSDownload',
  setup() {
    const currentScreen = ref(0);
    const steps = ['获取 UDID', '注册账号', '下载安装'];
    const isReturningUser = ref(false);

    const udid = ref(getUrlParam("udid"));
    const udidError = ref('');
    const mobile = ref('');
    const mobileError = ref('');
    const userName = ref('');
    const userNameError = ref('');
    const password = ref('');
    const passwordError = ref('');
    const agreed = ref(false);
    const submitting = ref(false);

    const toast = ref({ show: false, message: '' });

    // New state for automated UDID retrieval
    const tempToken = ref(getUrlParam("tempToken"));
    const isPolling = ref(false);
    const pollingTimer = ref(null);
    const profileDownloadUrl = ref(null);
    const udidReceived = ref(false);
    const isManualMode = ref(false);

    const enterpriseUrl = 'itms-services://?action=download-manifest&url=https://www.haoping.fun/download/ios/manifest-qiye.plist';
    const personalUrl = 'itms-services://?action=download-manifest&url=https://www.haoping.fun/download/ios/manifest.plist';

    const showToast = (msg) => {
      toast.value = { show: true, message: msg };
    };

    const stopPolling = () => {
      isPolling.value = false;
      if (pollingTimer.value) {
        clearInterval(pollingTimer.value);
        pollingTimer.value = null;
      }
    };

    const startPolling = () => {
      isPolling.value = true;

      pollingTimer.value = setInterval(async () => {
        if (!tempToken.value) return;

        try {
          const status = await API.ios.getTempTokenStatus(tempToken.value);

          if (status && status.received) {
            // UDID received from iOS callback!
            udid.value = status.udid;
            udidReceived.value = true;

            // Stop polling
            stopPolling();

            // Show success and proceed to registration
            showToast(`设备已识别: ${status.model || 'iOS Device'}`);

            // Move to registration screen
            goToScreen(2);
          }
        } catch (e) {
          console.error('Polling failed:', e);
        }
      }, 2000); // Poll every 2 seconds

      // Stop polling after 60 seconds (timeout)
      setTimeout(() => {
        if (isPolling.value) {
          stopPolling();
          if (!udidReceived.value) {
            showToast('获取超时，请重试或手动粘贴 UDID');
            goToScreenManual(1);
          }
        }
      }, 60000);
    };

    const startUdidRetrieval = async () => {
      try {
        const endpoint = config.api.endpoint;

        // Create profile session with backend
        const result = await API.ios.createProfileSession();

        if (result && result.tempToken) {
          tempToken.value = result.tempToken;
          profileDownloadUrl.value = result.downloadUrl;

          // Show loading state
          showToast('正在准备描述文件下载...');

          // Start polling before redirect
          startPolling();

          // Wait a moment then redirect
          setTimeout(() => {
            window.location.href = endpoint + profileDownloadUrl.value;
          }, 500);
        }
      } catch (e) {
        showToast('创建下载会话失败，请重试');
        console.error('Failed to create profile session:', e);
      }
    };

    const goToScreenManual = (screen) => {
      stopPolling(); // Stop any active polling
      isManualMode.value = true; // Enable manual mode
      goToScreen(screen);
    };

    const isValidUdid = computed(() => {
      const s = (udid.value || '').trim();
      return /^[0-9a-fA-F]{40}$/.test(s) || /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{16}$/.test(s);
    });

    const isValidPhone = (s) => /^1[3-9]\d{9}$/.test((s || '').trim());
    const isValidUsername = (s) => /^[a-zA-Z0-9_]{1,16}$/.test((s || '').trim());
    const isValidPassword = (s) => (s || '').trim().length >= 6;

    const canRegister = computed(() => {
      return isValidPhone(mobile.value) &&
        isValidUsername(userName.value) &&
        isValidPassword(password.value) &&
        !submitting.value;
    });

    const downloadTitle = computed(() => isReturningUser.value ? '个人签名下载' : '设备已绑定，开始下载');
    const downloadLead = computed(() => isReturningUser.value
      ? '个人签名包已就绪 — 无需额外信任设置，直接安装即可使用。'
      : '证书签发完成 — 现在可以安装他趣秒中 iOS 版了。');
    const downloadLabel = computed(() => isReturningUser.value ? 'PERSONAL BUILD' : 'ENTERPRISE BUILD');
    const downloadDesc = computed(() => isReturningUser.value
      ? '点击下方按钮，系统会弹出安装确认。个人签名包无需信任开发者，安装完成后即可直接打开使用。'
      : '点击下方按钮，系统会弹出安装确认。允许后，你的设备会自动开始下载安装。');
    const downloadCertTag = computed(() => isReturningUser.value ? '个人签名' : '企业证书');
    const downloadUrl = computed(() => isReturningUser.value ? personalUrl : enterpriseUrl);

    const navTitle = computed(() => {
      if (currentScreen.value === 1) return '获取 UDID';
      if (currentScreen.value === 2) return '注册账号';
      if (currentScreen.value === 3) return isReturningUser.value ? '个人签名下载' : '下载安装';
      return '';
    });

    const goBack = () => {
      if (currentScreen.value === 1) goToScreen(0);
      if (currentScreen.value === 2) goToScreen(1);
      if (currentScreen.value === 3) goToScreen(0);
    };

    const chooseNewDevice = async () => {
      isReturningUser.value = false;
      isManualMode.value = false; // Reset manual mode

      // Check if device already has registered UDID
      const url = new URL(location.href);
      const existingDeviceId = url.searchParams.get('deviceId');

      if (existingDeviceId) {
        try {
          const status = await API.ios.getUdidStatus(existingDeviceId);
          if (status && status.udidRegistered) {
            udid.value = status.udid;
            showToast('检测到已注册设备，直接进入下载');
            goToScreen(3);
            return;
          }
        } catch (e) {
          // Continue with normal flow
        }
      }

      // Go to description file installation guide screen
      goToScreen(1);
    };

    const chooseReturning = () => {
      isReturningUser.value = true;
      goToScreen(3);
    };

    const goToScreen = (n) => {
      if (n === 2 && !isValidUdid.value) return;
      currentScreen.value = n;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onUdidInput = () => {
      const v = udid.value.trim();
      if (!v) {
        udidError.value = '';
        return;
      }
      if (!isValidUdid.value) {
        udidError.value = 'UDID 格式不正确，应为 25 或 40 位字符';
      } else {
        udidError.value = '';
      }
    };

    const onMobileInput = () => {
      mobile.value = mobile.value.replace(/\D/g, '').slice(0, 11);
      if (!mobile.value) {
        mobileError.value = '';
        return;
      }
      if (!isValidPhone(mobile.value)) {
        mobileError.value = '请输入正确的 11 位手机号';
      } else {
        mobileError.value = '';
      }
    };

    const onUserNameInput = () => {
      const v = userName.value.trim();
      if (!v) {
        userNameError.value = '';
        return;
      }
      if (!isValidUsername(v)) {
        userNameError.value = '用户名 3-16 位，仅限字母/数字/下划线';
      } else {
        userNameError.value = '';
      }
    };

    const onPasswordInput = () => {
      const v = password.value;
      if (!v) {
        passwordError.value = '';
        return;
      }
      if (!isValidPassword(v)) {
        passwordError.value = '密码至少 6 位';
      } else {
        passwordError.value = '';
      }
    };

    const doRegister = async () => {
      if (!canRegister.value) return;
      submitting.value = true;
      try {
        const res = await API.user.doRegister({
          mobile: mobile.value.trim(),
          userName: userName.value.trim(),
          password: password.value,
          deviceId: udid.value.trim(),
          udid: udid.value.trim(),
        });
        if (res.success) {
          // If we have a temp token, bind it to the device
          if (tempToken.value && udid.value.trim()) {
            try {
              await API.ios.bindTempTokenToDevice(
                tempToken.value,
                udid.value.trim()
              );
              console.log('Temp token bound to device');
            } catch (e) {
              console.warn('Failed to bind temp token:', e);
              // Non-critical, continue
            }
          }

          localStorage.setItem('ios_udid_registered', 'true');
          localStorage.setItem('ios_udid', udid.value.trim());
          showToast('注册成功，UDID 已绑定企业证书');
          isReturningUser.value = res.data.udidRegistered;
          goToScreen(3);
        } else {
          showToast(res.errorMsg || '注册失败，请重试');
        }
      } catch (e) {
        showToast('网络错误，请重试');
      } finally {
        submitting.value = false;
      }
    };

    const onOpenUdid = () => {
      showToast('已在新窗口打开 udid.my，安装描述文件后回到本页粘贴');
    };

    const onDownload = () => {
      showToast('正在拉起系统安装弹窗…');
    };

    const copyLink = async () => {
      const link = isReturningUser.value ? personalUrl : enterpriseUrl;
      try {
        await navigator.clipboard.writeText(link);
        showToast('链接已复制，可粘贴到 Safari 地址栏打开');
      } catch (_) {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        showToast('链接已复制（兼容模式）');
      }
    };

    const resetFlow = () => {
      stopPolling(); // Stop any active polling
      currentScreen.value = 0;
      udid.value = '';
      mobile.value = '';
      userName.value = '';
      password.value = '';
      agreed.value = false;
      isReturningUser.value = false;
      isManualMode.value = false;
      tempToken.value = null;
      profileDownloadUrl.value = null;
      udidReceived.value = false;
      udidError.value = '';
      mobileError.value = '';
      userNameError.value = '';
      passwordError.value = '';
    };

    onMounted(() => {
      try {
        const url = new URL(location.href);

        // Check URL for both udid and tempToken (from iOS profile callback)
        const token = url.searchParams.get('tempToken');
        const u = url.searchParams.get('udid');

        // If both are present, we came from iOS callback - go directly to registration
        if (u && token && (/^[0-9a-fA-F]{40}$/.test(u) || /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{16}$/.test(u))) {
          udid.value = u;
          tempToken.value = token;
          onUdidInput();
          showToast('设备已识别，正在完成注册');
          goToScreen(2);
          return;
        }

        // If only tempToken, start polling for UDID
        if (token) {
          tempToken.value = token;
          startPolling();
          goToScreen(1);
          return;
        }

        // If only udid, use it directly (legacy URL format)
        if (u && (/^[0-9a-fA-F]{40}$/.test(u) || /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{16}$/.test(u))) {
          udid.value = u;
          onUdidInput();
          showToast('已从 URL 自动识别 UDID');
        }
      } catch (_) { /* ignore */ }
    });

    onUnmounted(() => {
      stopPolling();
    });

    return {
      currentScreen,
      steps,
      isReturningUser,
      udid,
      udidError,
      mobile,
      mobileError,
      userName,
      userNameError,
      password,
      passwordError,
      agreed,
      submitting,
      toast,
      downloadTitle,
      downloadLead,
      downloadLabel,
      downloadDesc,
      downloadCertTag,
      downloadUrl,
      isValidUdid,
      canRegister,
      navTitle,
      showToast,
      chooseNewDevice,
      chooseReturning,
      goToScreen,
      goToScreenManual,
      goBack,
      onUdidInput,
      onMobileInput,
      onUserNameInput,
      onPasswordInput,
      doRegister,
      onOpenUdid,
      onDownload,
      copyLink,
      resetFlow,
      // New properties for automated UDID retrieval
      tempToken,
      isPolling,
      profileDownloadUrl,
      udidReceived,
      isManualMode,
      startUdidRetrieval,
      startPolling,
      stopPolling,
    };
  },
};
</script>

<style lang="less" scoped>
.ios-download-page {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.ios-download-page *,
.ios-download-page *::before,
.ios-download-page *::after {
  box-sizing: border-box;
}

.stepper {
  padding: 18px 20px 4px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  background: #fff;
}

.step-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 14px;
  border-bottom: 2px solid #ebedf0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.step-pill .dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ebedf0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.step-pill .num {
  font-size: 12px;
}

.step-pill .check {
  width: 10px;
  height: 6px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translate(1px, -1px);
}

.step-pill .label {
  font-size: 12px;
  color: #969799;
  line-height: 1.2;
  transition: color 0.3s ease;
}

.step-pill.is-active {
  border-bottom-color: #07c160;
}

.step-pill.is-active .dot {
  background: #07c160;
  box-shadow: 0 0 0 4px #f1faf4;
}

.step-pill.is-active .label {
  color: #323233;
  font-weight: 600;
}

.step-pill.is-done {
  border-bottom-color: #07c160;
}

.step-pill.is-done .dot {
  background: #07c160;
}

.screen {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}

.hero {
  margin-bottom: 20px;
}

.hero .eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #07c160;
  font-weight: 600;
  margin-bottom: 8px;
}

.hero h1 {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px;
  color: #28272c;
  letter-spacing: -0.01em;
}

.hero .lead {
  font-size: 14px;
  color: #969799;
  margin: 0;
  line-height: 1.55;
}

/* Entry cards */
.entry-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;
}

.entry-card {
  appearance: none;
  border: 0;
  font-family: inherit;
  text-align: left;
  width: 100%;
  max-width: 100%;
  cursor: pointer;
  border-radius: 14px;
  padding: 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 0.15s ease, box-shadow 0.25s ease, border-color 0.2s ease;
  position: relative;
  overflow: hidden;
}

.entry-card:active {
  transform: scale(0.995);
}

.entry-card .head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.entry-card .icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.entry-card .title {
  font-size: 16px;
  font-weight: 600;
  color: #28272c;
}

.entry-card .desc {
  font-size: 13px;
  color: #969799;
  line-height: 1.5;
  padding-left: 52px;
}

.entry-card .arrow {
  margin-left: auto;
  opacity: 0.4;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.entry-card:hover .arrow {
  opacity: 0.7;
  transform: translateX(2px);
}

.entry-card.primary {
  background: linear-gradient(135deg, #07c160 0%, #059548 100%);
  color: #fff;
  box-shadow: 0 6px 24px rgba(7, 193, 96, 0.28);
}

.entry-card.primary .icon {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.entry-card.primary .title {
  color: #fff;
}

.entry-card.primary .desc {
  color: rgba(255, 255, 255, 0.85);
}

.entry-card.primary .arrow {
  opacity: 0.6;
  color: #fff;
}

.entry-card.secondary {
  background: #fff;
  border: 1.5px solid #ebedf0;
  color: #323233;
}

.entry-card.secondary .icon {
  background: #f7f8fa;
  color: #969799;
  border: 1px solid #ebedf0;
}

.entry-card.secondary:hover {
  border-color: #07c160;
  box-shadow: 0 2px 8px rgba(40, 39, 44, 0.06), 0 8px 24px rgba(40, 39, 44, 0.08);
}

.entry-card.secondary:hover .icon {
  background: #f1faf4;
  color: #07c160;
  border-color: rgba(7, 193, 96, 0.2);
}

.card {
  background: #f7f8fa;
  border-radius: 14px;
  padding: 16px;
  border: 1px solid #ebedf0;
}

.card-hint {
  font-size: 12.5px;
  color: #646566;
  line-height: 1.55;
}

.page-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #f2f3f5;
  position: relative;
}

.nav-back {
  position: absolute;
  left: 16px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #07c160;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 999px;
  transition: background 0.15s ease;
}

.nav-back:hover {
  background: #f1faf4;
}

.nav-title {
  font-size: 16px;
  font-weight: 600;
  color: #28272c;
}

.step-card {
  background: #f7f8fa;
  border-radius: 14px;
  padding: 14px 16px;
  border: 1px solid #ebedf0;
  margin: 6px 0 16px;
}

.step-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  align-items: flex-start;
  font-size: 13px;
  color: #646566;
  line-height: 1.6;
}

.step-item:first-child {
  padding-top: 4px;
}

.step-item + .step-item {
  border-top: 1px dashed #ebedf0;
}

.step-num {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid rgba(7, 193, 96, 0.3);
  color: #07c160;
  font-weight: 600;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.action-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  margin-top: 8px;
}

.btn-icon {
  width: 16px;
  height: 16px;
}

.field {
  margin-top: 18px;
  position: relative;
}

.field-label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  color: #646566;
  margin-bottom: 6px;
  font-weight: 500;
}

.field-label .req {
  color: #FF4081;
  margin-right: 2px;
}

.field-label .hint {
  color: #969799;
  font-weight: 400;
  font-size: 12px;
}

.field-error {
  color: #FF4081;
  font-size: 12px;
  margin-top: 6px;
  min-height: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-help {
  color: #969799;
  font-size: 12px;
  margin-top: 6px;
  line-height: 1.5;
}

.field-help code {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  color: #646566;
}

.warn-card {
  margin-top: 16px;
  background: #fffbe8;
  border: 1px solid rgba(255, 151, 106, 0.3);
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.warn-icon {
  color: #ff976a;
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.warn-text {
  font-size: 12.5px;
  color: #323233;
  line-height: 1.55;
}

.install-guide {
  margin-top: 16px;
  background: #f7f8fa;
  border-radius: 14px;
  padding: 18px;
  border: 1px solid #ebedf0;
}

.install-guide-title {
  font-size: 14px;
  font-weight: 600;
  color: #323233;
  margin-bottom: 14px;
}

.install-guide-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.guide-step {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.guide-step-num {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #07c160;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.guide-step-content {
  flex: 1;
}

.guide-step-content strong {
  font-size: 13px;
  color: #323233;
  font-weight: 600;
  display: block;
  margin-bottom: 2px;
}

.guide-step-content p {
  font-size: 12px;
  color: #969799;
  margin: 0;
  line-height: 1.5;
}

.install-guide-note {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #ebedf0;
  font-size: 12px;
  color: #969799;
  display: flex;
  align-items: center;
}

.edit-link {
  color: #07c160;
  font-size: 13px;
  cursor: pointer;
}

.chip-readonly {
  background: #f1faf4;
  border: 1px solid rgba(7, 193, 96, 0.25);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 12px;
  color: #323233;
  word-break: break-all;
  line-height: 1.5;
}

.lock-icon {
  flex: 0 0 18px;
  font-size: 18px;
  color: #07c160;
}

.stack {
  flex: 1;
  min-width: 0;
}

.chip-label {
  font-size: 11px;
  color: #07c160;
  margin-bottom: 2px;
  font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", "PingFang SC", "Microsoft YaHei", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.chip-value {
  font-weight: 500;
}

.agree-checkbox {
  margin-top: 18px;
  font-size: 13px;
  color: #646566;
}

.agree-checkbox a {
  color: #07c160;
  text-decoration: none;
}

.meta-row {
  display: flex;
  gap: 6px;
  align-items: center;
  color: #969799;
  font-size: 12px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.dot-sep {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #dcdee0;
}

.footer {
  flex-shrink: 0;
  padding: 14px 20px calc(14px + env(safe-area-inset-bottom));
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 22%);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 10;
}

.success-medal {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f1faf4;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px auto 16px;
  position: relative;
}

.success-medal::before {
  content: "";
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 1px dashed rgba(7, 193, 96, 0.35);
  animation: ringPulse 3s ease-in-out infinite;
}

@keyframes ringPulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.06); opacity: 0.9; }
}

.success-icon {
  font-size: 32px;
  color: #07c160;
}

.download-card {
  background: #fff;
  color: #28272c;
  border-radius: 20px;
  padding: 20px;
  margin-top: 14px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #ebedf0;
}

.download-card::after {
  content: "";
  position: absolute;
  right: -40px;
  top: -40px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(7, 193, 96, 0.04);
}

.download-card::before {
  content: "";
  position: absolute;
  right: 20px;
  bottom: -30px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(7, 193, 96, 0.03);
}

.download-label {
  font-size: 11px;
  color: #07c160;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  position: relative;
  z-index: 1;
}

.download-card h3 {
  margin: 4px 0 10px;
  font-size: 20px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.download-sub {
  font-size: 13px;
  color: #646566;
  line-height: 1.55;
  position: relative;
  z-index: 1;
}

.filemeta {
  display: flex;
  gap: 8px;
  margin-top: 14px;
  font-size: 11px;
  color: #969799;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

.filemeta span {
  background: #f7f8fa;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #ebedf0;
}

.install-steps {
  margin-top: 20px;
}

.install-steps .step {
  display: flex;
  gap: 12px;
  padding: 14px 0;
  align-items: flex-start;
  position: relative;
}

.install-steps .step + .step {
  border-top: 1px solid #ebedf0;
}

.step-n {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #07c160;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
}

.step-body {
  flex: 1;
  padding-top: 2px;
}

.step-body h4 {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #28272c;
}

.step-body p {
  margin: 0;
  font-size: 13px;
  color: #969799;
  line-height: 1.55;
}

.help-card {
  background: #f7f8fa;
  border-radius: 14px;
  padding: 18px;
  margin-top: 18px;
  text-align: center;
  border: 1px solid #ebedf0;
}

.help-title {
  font-size: 13px;
  color: #646566;
  margin-bottom: 10px;
}

.copy-btn {
  margin: 0 auto;
}

.help-text {
  font-size: 12px;
  color: #969799;
  margin-top: 10px;
}

.content{
  background: #ffffff;
  .van-field{
    border: 1px solid #ebedf0;
    border-radius: 4px;
  }
}

:deep(.van-field.is-invalid .van-field__control) {
  border-color: #FF4081;
}

.divider-or {
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: #969799;
  font-size: 13px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ebedf0;
  }

  span {
    padding: 0 16px;
  }
}

.manual-btn {
  width: 100%;
  height: 44px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
  }
}

.polling-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
  background: #f1faf4;
  border-radius: 14px;
  border: 1px solid rgba(7, 193, 96, 0.25);
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(7, 193, 96, 0.2);
  border-top-color: #07c160;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.polling-text {
  font-size: 13px;
  color: #646566;
  line-height: 1.55;

  strong {
    color: #323233;
  }
}

.manual-paste-section {
  margin-top: 16px;
}
</style>
